import { Component } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ApiService } from '../../api.service';

const DAY = 86400000;

@Component({
  selector: 'app-activity',
  templateUrl: './activity.component.html',
  styleUrls: ['./activity.component.css']
})
export class ActivityComponent {
  id = localStorage.getItem('user_id');
  user_id = parseInt(this.id as string, 10);
  isLoading = false;

  activities: any[] = [];
  cards: any[] = [];
  connections = 0;
  referrals = 0;

  range = 30;          // applied
  type = 'all';        // applied
  dRange = 30;         // filter panel draft
  dType = 'all';
  showAll = false;
  deletingId: any = null;

  list: any[] = [];
  line = ''; area = '';
  dots: any[] = []; yTicks: any[] = []; xLabels: any[] = [];
  segments: any[] = [];
  total = 0;

  types = [
    { key: 'view', label: 'Card Views', color: '#ff1493' },
    { key: 'share', label: 'Shares', color: '#ff5cc0' },
    { key: 'connect', label: 'New Connections', color: '#a24bff' },
    { key: 'message', label: 'Messages', color: '#4d8dff' },
    { key: 'other', label: 'Other', color: '#2ee6c8' }
  ];
  ranges = [7, 14, 30, 90];

  readonly C = 2 * Math.PI * 70;

  constructor(private apiService: ApiService) {
    this.load();
  }

  load() {
    this.isLoading = true;
    forkJoin({
      act: this.apiService.getActivity(this.user_id).pipe(catchError(() => of(null))),
      cards: this.apiService.getCards({ user_id: this.id, stripe_subscription_id: null, subscription_id: null }).pipe(catchError(() => of(null))),
      con: this.apiService.getContact(this.user_id).pipe(catchError(() => of(null))),
      refs: this.apiService.getUserReferals(this.user_id).pipe(catchError(() => of(null)))
    }).subscribe(({ act, cards, con, refs }: any) => {
      this.activities = act?.status == 'Success' ? (act.Activity || []) : [];
      this.cards = cards?.status == 'Success' ? (cards.Card || []) : [];
      this.connections = (con?.contacts || []).filter((c: any) => c.status === 'Accepted').length;
      const rr = refs?.referals ?? refs?.Referals ?? refs?.referrals ?? refs?.data ?? refs;
      this.referrals = Array.isArray(rr) ? rr.length : 0;
      this.build();
      this.isLoading = false;
    });
  }

  /* ---------- helpers ---------- */
  private ts(a: any): number {
    return a.created_at ? new Date(String(a.created_at).replace(' ', 'T')).getTime() : 0;
  }
  private sod(d: Date) { return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime(); }

  typeOf(a: any): string {
    const t = String(a.activity || '').toLowerCase();
    if (/message|chat/.test(t)) return 'message';
    if (/request|friend|connect|accept|contact|referral|joined/.test(t)) return 'connect';
    if (/shar|send|sent/.test(t)) return 'share';
    if (/view/.test(t)) return 'view';
    return 'other';
  }

  iconOf(a: any): string {
    const k = this.typeOf(a);
    if (k === 'view') return 'fa-solid fa-eye';
    if (k === 'share') return 'fa-solid fa-share-nodes';
    if (k === 'connect') return 'fa-solid fa-user-group';
    if (k === 'message') return 'fa-solid fa-comment-dots';
    const t = String(a.activity || '').toLowerCase();
    if (/live|stream|broadcast/.test(t)) return 'fa-solid fa-tower-broadcast';
    if (/call|video/.test(t)) return 'fa-solid fa-video';
    return 'fa-solid fa-bolt';
  }

  tagOf(a: any): string {
    const k = this.typeOf(a);
    return k === 'view' ? '+1 View' : k === 'share' ? '+1 Share' : k === 'connect' ? '+1 Connection' : k === 'message' ? 'Message' : '';
  }

  ago(a: any): string {
    const t = this.ts(a);
    if (!t) return '';
    const s = Math.floor((Date.now() - t) / 1000);
    if (s < 60) return 'just now';
    if (s < 3600) return Math.floor(s / 60) + ' minutes ago';
    if (s < 86400) return Math.floor(s / 3600) + ' hours ago';
    if (s < 86400 * 7) return Math.floor(s / 86400) + ' days ago';
    return new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  /* ---------- stats ---------- */
  get totalViews() { return this.cards.reduce((s, c) => s + (+c.totalViews || 0), 0); }
  get totalShares() { return this.cards.reduce((s, c) => s + (+c.sends || 0), 0); }

  get topCards() {
    return [...this.cards].sort((a, b) => (+b.totalViews || 0) - (+a.totalViews || 0)).slice(0, 4);
  }
  cardName(c: any) { return c.infoFormData?.cardTitle || ((c.infoFormData?.firstName || '') + ' ' + (c.infoFormData?.lastName || '')).trim() || 'Card'; }

  /* ---------- filters ---------- */
  onRange() { this.dRange = this.range; this.build(); }
  applyFilters() { this.range = +this.dRange; this.type = this.dType; this.build(); }
  resetFilters() { this.range = 30; this.type = 'all'; this.dRange = 30; this.dType = 'all'; this.build(); }

  get recent() { return this.showAll ? this.list : this.list.slice(0, 7); }

  /* ---------- chart + donut ---------- */
  build() {
    const today = this.sod(new Date());
    const from = today - (this.range - 1) * DAY;

    this.list = this.activities
      .filter(a => this.ts(a) >= from && (this.type === 'all' || this.typeOf(a) === this.type))
      .sort((a, b) => this.ts(b) - this.ts(a) || b.id - a.id);

    // line chart
    const counts = new Array(this.range).fill(0);
    for (const a of this.list) {
      const i = Math.floor((this.sod(new Date(this.ts(a))) - from) / DAY);
      if (i >= 0 && i < this.range) counts[i]++;
    }
    const max = Math.ceil(Math.max(...counts, 1) / 4) * 4;
    const W = 700, H = 260, L = 44, R = 14, T = 14, B = 30;
    const iw = W - L - R, ih = H - T - B;
    const pts = counts.map((c, i) => ({
      x: L + (this.range === 1 ? iw / 2 : (i * iw) / (this.range - 1)),
      y: T + ih - (c / max) * ih, c
    }));
    this.dots = pts;
    this.line = pts.map(p => p.x + ',' + p.y).join(' ');
    this.area = `M${L},${T + ih} L` + pts.map(p => p.x + ',' + p.y).join(' L') + ` L${pts[pts.length - 1].x},${T + ih} Z`;
    this.yTicks = [0, 1, 2, 3, 4].map(i => ({ v: Math.round((max * i) / 4), y: T + ih - (ih * i) / 4, x1: L, x2: W - R }));
    const step = Math.ceil(this.range / 6);
    this.xLabels = pts
      .map((p, i) => ({ x: p.x, i }))
      .filter(p => p.i % step === 0)
      .map(p => ({ x: p.x, t: new Date(from + p.i * DAY).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), y: H - 8 }));

    // donut
    const by: any = {};
    this.list.forEach(a => { const k = this.typeOf(a); by[k] = (by[k] || 0) + 1; });
    this.total = this.list.length;
    let acc = 0;
    this.segments = this.types.map(t => {
      const n = by[t.key] || 0;
      const len = this.total ? (n / this.total) * this.C : 0;
      const seg = { ...t, n, pct: this.total ? Math.round((n / this.total) * 100) : 0, dash: len + ' ' + (this.C - len), off: -acc };
      acc += len;
      return seg;
    });
  }

  deleteActivity(a: any) {
    if (!confirm('Delete this activity?')) return;
    this.deletingId = a.id;
    this.apiService.deleteActivity(this.user_id, a.id).subscribe(
      (r: any) => {
        this.deletingId = null;
        if (r.status == 'Success') { this.activities = this.activities.filter(x => x.id !== a.id); this.build(); }
        else alert('Could not delete the activity.');
      },
      () => { this.deletingId = null; alert('Could not delete the activity.'); }
    );
  }
}